import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { DatePipe, NgClass, NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { OurAuthService } from 'app/core/auth/auth.service';
import { MessagesService } from 'app/layout/common/messages/messages.service';
import { Message } from 'app/layout/common/messages/messages.types';
import { getNotificationModuleIcon } from 'app/layout/common/notifications/notification-module.icons';
import { NotificationToastService } from 'app/layout/common/notification-toast/notification-toast.service';
import { NotificationToast, NOTIFICATION_TOAST_DURATION } from 'app/layout/common/notification-toast/notification-toast.types';
import { BehaviorSubject, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { NotificationsService } from '../notifications/notifications.service';
import { environment } from 'environments/environment';
import { CustomAlertService } from 'app/modules/shared/services/alert.service';
import { AgentService } from 'app/company/ai-agents/agents.service';
import { dataSourceInternalTableCommands, dataSourceTablesListPath } from 'app/company/data-sources/data-source-table-route.util';
import { agentChatLink } from 'app/company/ai-chat/agent-chat/conversation/conversation-url.util';
import {
    CRM_CATALOGUE_NOTIFICATION_ROUTE,
    normalizeCrmCatalogueNotificationTag,
    unreadNotificationArrivals,
} from 'app/company/crm/shared/crm-catalogue-notification.util';

@Component({
    selector: 'messages',
    templateUrl: './messages.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'messages',
    standalone: true,
    imports: [TranslocoModule, MatButtonModule, NgIf, MatIconModule, MatTooltipModule, NgFor, NgClass, RouterLink, DatePipe],
    styles: [`
        .sai-notifications-panel {
            background: var(--sai-surface);
            color: var(--sai-text);
        }
        .sai-notifications-panel__surface {
            background: var(--sai-surface);
        }
        .sai-notifications-panel__divider {
            border-color: var(--sai-border);
        }
        .notification-panel-text {
            min-width: 0;
            max-width: 100%;
            overflow-wrap: anywhere;
            word-break: break-word;
        }
        .sai-notifications-panel__title {
            color: var(--sai-text);
        }
        .sai-notifications-panel__muted {
            color: var(--sai-text-secondary);
        }
        .sai-notifications-panel__icon-badge {
            background: var(--sai-surface-alt);
        }
        .sai-notifications-panel__icon-badge-glyph {
            color: var(--sai-text-secondary) !important;
        }
        .sai-notifications-panel__tablist {
            background: var(--sai-surface-alt);
        }
        .sai-notifications-panel__tab {
            color: var(--sai-text-secondary);
        }
        .sai-notifications-panel__tab--active {
            background: var(--sai-surface);
            color: var(--sai-text);
            box-shadow: var(--sai-shadow-xs);
        }
        .sai-notifications-panel__tab-badge {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-width: 1.25rem;
            height: 1.25rem;
            padding: 0 0.25rem;
            border-radius: 0.25rem;
            background: color-mix(in srgb, var(--sai-border) 65%, transparent);
            color: var(--sai-text-secondary);
            font-size: 0.6875rem;
            font-weight: 600;
            line-height: 1;
        }
        .sai-notifications-panel__tab--active .sai-notifications-panel__tab-badge {
            background: var(--sai-surface-alt);
        }
        .sai-notifications-panel__card--read {
            background: var(--sai-surface);
            border-color: var(--sai-border);
        }
        .sai-notifications-panel__card--unread {
            background: color-mix(in srgb, var(--c1, #3dc3fb) 10%, transparent);
            border-color: color-mix(in srgb, var(--c1, #3dc3fb) 40%, transparent);
            box-shadow: inset 3px 0 0 color-mix(in srgb, var(--c1, #3dc3fb) 75%, transparent);
        }
        .sai-notifications-panel__icon-btn {
            width: 1.75rem !important;
            height: 1.75rem !important;
            min-height: 1.75rem !important;
            padding: 0 !important;
        }
        /* The touch target must not exceed the visible box, or the Remove target overlaps
           the adjacent expand chevron and keeps its hover/tooltip (and steals clicks). */
        .sai-notifications-panel__icon-btn .mat-mdc-button-touch-target {
            width: 100% !important;
            height: 100% !important;
        }
        .sai-notifications-panel__icon-btn .mat-icon {
            color: var(--sai-text-secondary);
        }
        .sai-notifications-panel__okay-btn,
        .sai-notifications-panel__center-btn {
            border-color: var(--sai-border) !important;
            color: var(--sai-text) !important;
        }
    `],
})
export class MessagesComponent implements OnInit, OnDestroy {
    @ViewChild('messagesOrigin') private _messagesOrigin: MatButton;
    @ViewChild('messagesPanel') private _messagesPanel: TemplateRef<any>;


    messages: Message[];
    activeFilter: 'all' | 'unread' | 'read' = 'all';
    expandedIds = new Set<string>();
    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private _isFirstNotificationPayload = true;
    lang: string = 'en';
    counter: number = 0;
    isMuted: boolean;

    get unreadMessages(): Message[] {
        return (this.messages ?? []).filter(message => !message.read);
    }

    get readMessages(): Message[] {
        return (this.messages ?? []).filter(message => message.read);
    }

    get unreadCount(): number {
        return this.unreadMessages.length;
    }

    get allCount(): number {
        return (this.messages ?? []).length;
    }

    get unreadTabCount(): number {
        return this.unreadCount;
    }

    get readTabCount(): number {
        return this.readMessages.length;
    }

    get filteredMessages(): Message[] {
        const list = this.messages ?? [];
        if (this.activeFilter === 'unread') {
            return list.filter(m => !m.read);
        }
        if (this.activeFilter === 'read') {
            return list.filter(m => m.read);
        }
        return list;
    }

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _messagesService: MessagesService,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        public snackBar: MatSnackBar,
        private route: ActivatedRoute,
        private _router: Router,
        private _translocoService: TranslocoService,
        private _alertService: CustomAlertService,
        private _notificationService: NotificationsService
    ) {
        this.isMuted = this._notificationService.isMuted;
    }

    isAdmin: boolean = false;
    private readonly _authService = inject(OurAuthService);
    private readonly _agentService = inject(AgentService);
    private readonly _toastService = inject(NotificationToastService);
    private notificationSocket: WebSocket | null = null;

    ngOnInit(): void {
        this.lang = this._translocoService.getActiveLang();
        this._translocoService.langChanges$.pipe(takeUntil(this._unsubscribeAll)).subscribe((res) => {
            this.lang = res;
            this._changeDetectorRef.markForCheck();
        });
        // Close the notifications socket immediately on logout (do not wait for
        // layout destroy) so signed-out sessions stop receiving bell updates.
        this._authService.isLoggedIn$
            .pipe(distinctUntilChanged(), takeUntil(this._unsubscribeAll))
            .subscribe((loggedIn: boolean): void => {
                if (!loggedIn) {
                    this.closeNotificationSocket();
                    this._toastService.clear();
                }
            });

        // A toast is a shortcut into the bell list, so the click runs the exact
        // same routing + mark-as-read path a click inside the panel would.
        this._toastService.activated$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((toast: NotificationToast): void => {
                const message = (this.messages ?? []).find(item => String(item.id) === toast.id);
                if (message) {
                    this.navigateToMessage(message);
                }
            });

        this.getNotifications();
    }

    getNotifications() {
        this.closeNotificationSocket();
        const authToken = this._authService.accessToken;
        if (authToken) {
            const url = `${environment.socketLink}notifications/socket?token=${authToken}`;

            this.notificationSocket = new WebSocket(url);
            this.notificationSocket.onclose = () => {
                // Intentionally no auto-reconnect — reconnect only via getNotifications().
            };
            this.notificationSocket.onopen = () => {
                this.notificationSocket?.send('data');
            };

            this.notificationSocket.onmessage = (event) => {
                if (!this._authService.accessToken) {
                    return;
                }
                const previousMessages = this.messages ?? [];
                // The socket replays the whole list on connect; that first frame is
                // backlog, not news, so it must neither beep nor raise toasts.
                const isFirstPayload = this._isFirstNotificationPayload;
                this._isFirstNotificationPayload = false;
                try {
                    const parsed = JSON.parse(event.data);
                    const nextMessages = this.normalizeSocketNotifications(parsed);
                    this.messages = nextMessages;
                    if (
                        this.shouldPlayNotificationSound(
                            parsed,
                            previousMessages,
                            nextMessages,
                            isFirstPayload
                        )
                    ) {
                        this._notificationService.playBeep().catch(() => {});
                    }
                    if (!isFirstPayload) {
                        this.surfaceToasts(previousMessages, nextMessages);
                    }
                } catch {
                    this.messages = [];
                }
                this._changeDetectorRef.markForCheck();
            };

            this.notificationSocket.onerror = (error) => {
                console.error('WebSocket error:', error);
            };
        }
    }

    /**
     * WebSocket may send a bare array, or { data | notifications | items | results },
     * and each row may use message/body/content or createdAt/created_at/time.
     */
    private normalizeSocketNotifications(payload: unknown): Message[] {
        const rawList = this.extractNotificationArray(payload);
        return rawList.map((item, index) => this.normalizeNotificationRow(item, index));
    }

    private extractNotificationArray(payload: unknown): unknown[] {
        if (Array.isArray(payload)) {
            return payload;
        }
        if (payload && typeof payload === 'object') {
            const o = payload as Record<string, unknown>;
            const nested = [o.data, o.notifications, o.items, o.results];
            for (const n of nested) {
                if (Array.isArray(n)) {
                    return n;
                }
                if (n && typeof n === 'object' && Array.isArray((n as Record<string, unknown>).data)) {
                    return (n as Record<string, unknown>).data as unknown[];
                }
            }
        }
        return [];
    }

    private normalizeNotificationRow(item: unknown, index: number): Message {
        const fallbackId = `notification-${index}`;
        const originalRow = (item && typeof item === 'object') ? item as Record<string, unknown> : null;
        if (item === null || typeof item !== 'object') {
            return {
                id: fallbackId,
                read: false,
                time: new Date().toISOString(),
                description: '',
            } as Message;
        }
        let row = item as Record<string, unknown>;
        const wrapped = row['value'];
        if (wrapped && typeof wrapped === 'object') {
            const inner = wrapped as Record<string, unknown>;
            const outerId = row['id'];
            row = {
                ...inner,
                id: outerId !== undefined && outerId !== null ? outerId : inner.id,
                entityId: inner.id,
            } as Record<string, unknown>;
        }
        const title = this.firstString(row, ['title']) ?? '';
        const messageBody = this.firstString(row, [
            'message', 'body', 'content', 'text', 'description', 'summary',
        ]) ?? '';
        const text = messageBody || title;
        const created = this.pickCreatedAt(row);
        const id = String(row.id ?? row._id ?? row.uuid ?? fallbackId);
        const tag = this.firstString(row, ['tag', 'type', 'category']);
        const reportId = row.report_id ?? row.reportId ?? row.reportID;
        const entityId = row['entityId'];
        const resolvedRead =
            this.pickFirstDefinedBoolean([
                originalRow?.['isRead'],
                originalRow?.['is_read'],
                originalRow?.['read'],
                row.isRead,
                row.is_read,
                row.read,
            ]) ?? false;

        return {
            ...row,
            id,
            read: resolvedRead,
            time: typeof created === 'string' ? created : String(created),
            title: title || undefined,
            message: text,
            description: messageBody || title || undefined,
            createdAt: created,
            tag,
            report_id: reportId as string | number | undefined,
            reportId: reportId as string | number | undefined,
            entityId: entityId !== undefined && entityId !== null ? entityId as string | number : undefined,
        } as Message;
    }

    setActiveFilter(filter: 'all' | 'unread' | 'read'): void {
        this.activeFilter = filter;
        this._changeDetectorRef.markForCheck();
    }

    toggleExpand(message: Message): void {
        const id = String(message.id);
        if (this.expandedIds.has(id)) {
            this.expandedIds.delete(id);
        } else {
            this.expandedIds.add(id);
        }
        this._changeDetectorRef.markForCheck();
    }

    isExpanded(message: Message): boolean {
        return this.expandedIds.has(String(message.id));
    }

    getNotificationTitle(message: Message): string {
        const record = message as unknown as Record<string, unknown>;
        const title = this.firstString(record, ['title']);
        if (title) {
            return title;
        }
        const body = this.getNotificationBody(message);
        if (body) {
            return body.length > 120 ? `${body.slice(0, 120).trim()}...` : body;
        }
        return '';
    }

    getNotificationBody(message: Message): string {
        const record = message as unknown as Record<string, unknown>;
        const title = this.firstString(record, ['title']);
        const body = this.firstString(record, [
            'message', 'description', 'body', 'content', 'text', 'summary',
        ]);
        if (body && title && body !== title) {
            return body;
        }
        return body || title || '';
    }

    getNotificationPreview(message: Message): string {
        const title = this.firstString(message as unknown as Record<string, unknown>, ['title']);
        const body = this.firstString(message as unknown as Record<string, unknown>, [
            'message', 'description', 'body', 'content',
        ]);
        if (title && body && body !== title) {
            return body.length > 60 ? `${body.slice(0, 60).trim()}...` : body;
        }
        return '';
    }

    getNotificationIconStyle(message: Message): { icon: string; bg: string; text: string } {
        const record = message as unknown as Record<string, unknown>;
        const rawTag = this.firstString(record, ['tag', 'type', 'category']) ?? '';
        const tag = this.normalizeNotificationTag(rawTag);
        return getNotificationModuleIcon(tag);
    }

    acknowledgeNotification(message: Message, event?: MouseEvent): void {
        event?.stopPropagation();
        this.markMessageAsRead(message);
        this.expandedIds.delete(String(message.id));
        this._changeDetectorRef.markForCheck();
    }

    private firstString(obj: Record<string, unknown>, keys: string[]): string | undefined {
        for (const k of keys) {
            const v = obj[k];
            if (v !== undefined && v !== null && String(v).trim() !== '') {
                return String(v);
            }
        }
        return undefined;
    }

    private pickFirstDefinedBoolean(values: unknown[]): boolean | null {
        for (const value of values) {
            if (value === undefined || value === null) {
                continue;
            }
            if (typeof value === 'boolean') {
                return value;
            }
            const normalized = String(value).trim().toLowerCase();
            if (normalized === 'true' || normalized === '1') {
                return true;
            }
            if (normalized === 'false' || normalized === '0') {
                return false;
            }
        }
        return null;
    }

    private pickCreatedAt(row: Record<string, unknown>): string {
        const keys = ['createdAt', 'created_at', 'timestamp', 'time', 'date', 'created'];
        for (const k of keys) {
            const v = row[k];
            if (v === undefined || v === null) {
                continue;
            }
            if (typeof v === 'number' && !Number.isNaN(v)) {
                const ms = v > 1e12 ? v : v * 1000;
                return new Date(ms).toISOString();
            }
            const s = String(v).trim();
            if (s) {
                if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(s)) {
                    return s.replace(' ', 'T');
                }
                return s;
            }
        }
        return new Date().toISOString();
    }

    private shouldPlayNotificationSound(
        payload: unknown,
        previousMessages: Message[],
        nextMessages: Message[],
        isFirstPayload: boolean
    ): boolean {
        if (isFirstPayload) {
            return false;
        }
        if (this.isMuted || this._notificationService.isMuted) {
            return false;
        }
        if (
            payload &&
            typeof payload === 'object' &&
            !Array.isArray(payload) &&
            String((payload as Record<string, unknown>).type ?? '')
                .toLowerCase()
                .trim() === 'notification'
        ) {
            return true;
        }
        const previousIds = new Set((previousMessages ?? []).map(m => String(m.id)));
        return (nextMessages ?? []).some(m => !previousIds.has(String(m.id)));
    }

    /**
     * Raise a toast for every unread notification the socket just added, so an
     * arrival is visible without opening the bell. Ordered oldest-first because
     * the toast stack puts the newest on top.
     */
    private surfaceToasts(previousMessages: Message[], nextMessages: Message[]): void {
        unreadNotificationArrivals(previousMessages ?? [], nextMessages ?? [])
            .filter(message => this.getNotificationTitle(message).trim() !== '')
            .reverse()
            .forEach(message => this._toastService.show(this.buildToast(message)));
    }

    private buildToast(message: Message): NotificationToast {
        const title = this.getNotificationTitle(message);
        const body = this.getNotificationBody(message);
        const iconStyle = this.getNotificationIconStyle(message);

        return {
            id: String(message.id),
            title,
            // Only a second line when it actually says something new.
            body: body && body !== title ? body : undefined,
            icon: iconStyle.icon,
            iconBgClass: iconStyle.bg,
            iconTextClass: iconStyle.text,
            actionable: this.resolveNotificationRoute(message) !== null,
            duration: NOTIFICATION_TOAST_DURATION,
        };
    }

    markMessageAsRead(message: Message, event?: MouseEvent): void {
        event?.stopPropagation();

        if (message.read) {
            return;
        }

        const modal = {
            id: message.id,
            tag: String(message.tag ?? ''),
        };

        this._messagesService.readOne(modal).pipe(takeUntil(this._unsubscribeAll)).subscribe({
            next: () => {
                message.read = true;
                this._changeDetectorRef.markForCheck();
            },
            error: (error) => {
                this._alertService.failedMessage(error.error?.message ?? error.message);
            },
        });
    }

    navigateToMessage(message: Message, event?: MouseEvent): void {
        event?.stopPropagation();

        const rawTag = this.firstString(message as unknown as Record<string, unknown>, [
            'tag',
            'type',
            'category',
            'notification_type',
            'notificationType',
            'module',
            'module_type',
        ])?.toLowerCase().trim() ?? '';
        const route = this.resolveNotificationRoute(message);
        const agentId = this.pickFirstValue(message, ['agent_id', 'agentId']);
        if (agentId !== null) {
            this._agentService.setSelectedAgentId(String(agentId));
        }
        if (route) {
            this._router.navigateByUrl(route).catch((err) => {
                if (rawTag === 'data_source' || rawTag === 'ds' || rawTag === 'procedure' || rawTag === 'procedure_execution') {
                    console.error('[notifications][data-source] navigation error:', err);
                }
            });
        } else if (rawTag === 'data_source' || rawTag === 'ds' || rawTag === 'procedure' || rawTag === 'procedure_execution') {
            console.warn('[notifications][data-source] no route resolved');
        }
        this.markMessageAsRead(message);
        this.closePanel();
        this._changeDetectorRef.markForCheck();
    }

    removeOneMessage(message: Message, event: MouseEvent): void {
        event.stopPropagation();
        const modal = {
            id: message.id,
            tag: String(message.tag ?? ''),
        };
        this._messagesService
            .deleteOne(modal)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(
                () => {
                    this.messages = (this.messages ?? []).filter(
                        m => m.id !== message.id
                    );
                    this._changeDetectorRef.markForCheck();
                },
                (error) => {
                    this._alertService.failedMessage(error.error.message);
                }
            );
    }

    private resolveNotificationRoute(message: Message): string | null {
        const rawTag = this.firstString(message as unknown as Record<string, unknown>, [
            'tag',
            'type',
            'category',
            'notification_type',
            'notificationType',
            'module',
            'module_type',
        ])?.toLowerCase().trim() ?? '';
        const directLink = this.firstString(message as unknown as Record<string, unknown>, [
            'link',
            'url',
            'path',
            'route',
        ]);
        if (directLink && directLink.startsWith('/')) {
            return directLink;
        }
        const tag = this.normalizeNotificationTag(rawTag);
        const reportId = this.pickFirstValue(message, [
            'report_id',
            'reportId',
            'entityId',
            'id',
        ]);
        const activityId = this.pickFirstValue(message, ['entityId', 'id']);
        const dataSourceId = this.pickFirstValue(message, [
            'ds_id',
            'dsId',
            'data_source_id',
            'dataSourceId',
            'entityId',
            'id',
        ]);
        const taskId = this.pickFirstValue(message, [
            'task_id',
            'taskId',
            'entityId',
            'id',
        ]);
        const chatId = this.pickFirstValue(message, [
            'chat_id',
            'chatId',
            'entityId',
            'id',
        ]);
        const agentId = this.pickFirstValue(message, ['agent_id', 'agentId']);
        const sessionId = this.pickFirstValue(message, ['session_id', 'sessionId']);

        switch (tag) {
            case 'report':
                return '/reports';
            case 'question':
                if (activityId !== null) {
                    return `/activities?openQuestion=1&questionId=${activityId}`;
                }
                return '/activities';
            case 'data_source':
                if (dataSourceId !== null) {
                    return dataSourceTablesListPath(dataSourceId);
                }
                return '/data-sources';
            case 'team_task':
                if (taskId !== null) {
                    return `/tasks?openTask=1&taskId=${taskId}`;
                }
                return '/tasks';
            case 'automation_delivery':
                // Delivery notifications carry session + agent eid so the toast opens the
                // recipient's durable thread, not an empty agent-chat shell.
                if (agentId !== null && sessionId !== null) {
                    return agentChatLink({ sessionId });
                }
                if (sessionId !== null) {
                    return agentChatLink({ sessionId });
                }
                if (agentId !== null) {
                    return agentChatLink({});
                }
                return null;
            case 'automation':
                // Author alert that a run could not finish: open the automation detail.
                if (activityId !== null) {
                    return `/automations/${activityId}`;
                }
                return '/automations';
            case 'ai_chat_share':
                if (sessionId !== null && agentId !== null) {
                    return agentChatLink({ sessionId });
                }
                if (sessionId !== null) {
                    return agentChatLink({ sessionId });
                }
                if (chatId !== null) {
                    return agentChatLink({ sessionId: chatId });
                }
                if (agentId !== null) {
                    return agentChatLink({});
                }
                return null;
            case 'procedure':
                if (dataSourceId !== null) {
                    return dataSourceTablesListPath(dataSourceId);
                }
                return '/data-sources';
            case 'favorites_shared': {
                const sharedMessageId = this.pickFirstValue(message, [
                    'entityId',
                    'message_id',
                    'messageId',
                    'shared_message_id',
                    'sharedMessageId',
                    'id',
                ]);
                if (sharedMessageId !== null) {
                    return `/overview/favorites?favoriteTab=shared&sharedMessageId=${sharedMessageId}&openSharedMessage=1`;
                }
                return '/overview/favorites?favoriteTab=shared';
            }
            case 'crm_catalogue':
                return CRM_CATALOGUE_NOTIFICATION_ROUTE;
            default:
                return null;
        }
    }

    private normalizeNotificationTag(tag: string): string {
        const value = (tag ?? '').trim().toLowerCase();
        if (value === 'ds' || value === 'datasource' || value === 'data_sources') {
            return 'data_source';
        }
        if (value === 'data') {
            return 'data_source';
        }
        if (
            value === 'upload_table' ||
            value === 'data_source' ||
            value === 'upload_tables' ||
            value === 'table_upload' ||
            value === 'table_uploaded' ||
            value === 'uploaded_table'
        ) {
            return 'data_source';
        }
        if (value === 'questions') {
            return 'question';
        }
        if (
            value === 'ask_question' ||
            value === 'ask_questions' ||
            value === 'question_asked'
        ) {
            return 'question';
        }
        if (
            value === 'report_creation' ||
            value === 'report_created' ||
            value === 'create_report'
        ) {
            return 'report';
        }
        if (value === 'task' || value === 'tasks') {
            return 'team_task';
        }
        if (
            value === 'giving_task' ||
            value === 'giving_tasks' ||
            value === 'task_given' ||
            value === 'assigned_task' ||
            value === 'assign_task'
        ) {
            return 'team_task';
        }
        if (value === 'ai_chat' || value === 'ai-chat' || value === 'aichat') {
            return 'ai_chat_share';
        }
        if (
            value === 'automation_delivery' ||
            value === 'automation-delivery' ||
            value === 'automationdelivery'
        ) {
            return 'automation_delivery';
        }
        if (value === 'automations') {
            return 'automation';
        }
        if (
            value === 'share_message' ||
            value === 'shared_message' ||
            value === 'chat_share' ||
            value === 'shared_response'
        ) {
            return 'favorites_shared';
        }
        const crmCatalogue = normalizeCrmCatalogueNotificationTag(value);
        if (crmCatalogue) {
            return crmCatalogue;
        }
        if (value === 'procedures') {
            return 'procedure';
        }
        if (
            value === 'procedure_execution' ||
            value === 'execute_procedure' ||
            value === 'procedure_executed'
        ) {
            return 'procedure';
        }
        return value;
    }

    private pickFirstValue(message: Message, keys: string[]): string | number | null {
        for (const key of keys) {
            const value = (message as any)?.[key];
            if (value !== undefined && value !== null && String(value).trim() !== '') {
                return value as string | number;
            }
        }
        return null;
    }

    ngOnDestroy(): void {
        this.closeNotificationSocket();
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

        if (this._overlayRef) {
            this._overlayRef.dispose();
        }

    }

    /** Tear down the notifications WebSocket without scheduling reconnect. */
    private closeNotificationSocket(): void {
        const socket = this.notificationSocket;
        this.notificationSocket = null;
        if (!socket) {
            return;
        }
        socket.onopen = null;
        socket.onmessage = null;
        socket.onerror = null;
        socket.onclose = null;
        try {
            socket.close();
        } catch {
            // Already closed.
        }
    }

    markAsRead(help) {

    }

    viewOrderDetails(order) {

    }
    openPanel(): void {
        if (!this._messagesPanel || !this._messagesOrigin) {
            return;
        }
        if (!this._overlayRef) {
            this._createOverlay();
        }

        // Attach the portal to the overlay
        this._overlayRef.attach(new TemplatePortal(this._messagesPanel, this._viewContainerRef));
    }

    closePanel(): void {
        // navigateToMessage() also runs for toast clicks, where the panel was
        // never opened and no overlay exists yet.
        this._overlayRef?.detach();
    }


    markAllAsRead(): void {
        if (this.messages.length > 0) {
            this._messagesService.readAll().pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
                this.messages = (this.messages ?? []).map(message => ({ ...message, read: true }));
                this._changeDetectorRef.markForCheck();
            }, (error) => {
                this._alertService.failedMessage(error.error.message);
            });
        }
    }

    deleteAllNotifications(): void {
        if(this.messages.length > 0){
            this._messagesService.deleteAll().pipe(takeUntil(this._unsubscribeAll)).subscribe((res) => {
                this.messages = [];
                this.closePanel();
                this._changeDetectorRef.markForCheck();
            }, (error) => {
                this._alertService.failedMessage(error.error.message)
            })
        }
    }

    toggleRead(message: Message): void {
        message.read = !message.read;
        this._messagesService.update(message.id, message).subscribe();
    }

    delete(message: Message): void {
        this._messagesService.delete(message.id).subscribe();
    }

    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    private _createOverlay(): void {
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'fuse-backdrop-on-mobile',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay.position()
                .flexibleConnectedTo(this._messagesOrigin._elementRef.nativeElement)
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top',
                    },
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'bottom',
                    },
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                    },
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom',
                    },
                ]),
        });

        // Detach the overlay from the portal on backdrop click
        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
        });
    }

}
